/**
 * Entity Manager - Manages all entities in a scene
 * 
 * Features:
 * - Entity spawning and lifecycle
 * - Entity queries by tag, type, position
 * - Update loop management
 * - Pooling for performance
 */

import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

import { IDisposable } from "../core/Engine.types";
import { AssetManager } from "../assets/AssetManager";
import { Entity } from "./Entity";
import { EntityConfig, EntityState } from "./Entity.types";

/**
 * Entity spawn options
 */
export interface SpawnOptions extends EntityConfig {
    /** Initialize immediately after spawning */
    autoInitialize?: boolean;
    /** Setup physics after model is loaded */
    autoPhysics?: boolean;
    /** Start playing default animation */
    autoAnimation?: boolean;
}

/**
 * EntityManager - Manages all entities in a scene
 */
export class EntityManager implements IDisposable {
    private _scene: Scene;
    private _assetManager: AssetManager;
    private _entities: Map<string, Entity> = new Map();
    private _entitiesByTag: Map<string, Set<string>> = new Map();
    private _activeEntities: Set<string> = new Set();

    /**
     * Get total entity count
     */
    public get entityCount(): number {
        return this._entities.size;
    }

    /**
     * Get active entity count
     */
    public get activeEntityCount(): number {
        return this._activeEntities.size;
    }

    constructor(scene: Scene, assetManager: AssetManager) {
        this._scene = scene;
        this._assetManager = assetManager;
    }

    /**
     * Spawn a new entity
     */
    public async spawn(options: SpawnOptions): Promise<Entity> {
        const entity = new Entity(this._scene, options);

        // Load and attach model if specified
        if (options.modelId) {
            try {
                const modelAsset = await this._assetManager.loadModelFromCatalog(options.modelId);
                entity.attachModel(modelAsset);

                // Setup physics if configured
                if (options.autoPhysics && options.physics?.enabled) {
                    entity.setupPhysics(
                        options.physics.shape,
                        options.physics.mass,
                        {
                            friction: options.physics.friction,
                            restitution: options.physics.restitution,
                        }
                    );
                }

                // Play default animation if configured
                if (options.autoAnimation && options.animation?.defaultAnimation) {
                    entity.playAnimation(
                        options.animation.defaultAnimation,
                        options.animation.loop ?? true
                    );
                }
            } catch (error) {
                console.error(`EntityManager: Failed to load model "${options.modelId}" for entity "${options.name}"`, error);
            }
        }

        // Register entity
        this._registerEntity(entity);

        // Initialize if requested
        if (options.autoInitialize !== false) {
            await entity.initialize();
            this._activeEntities.add(entity.id);
        }

        console.log(`EntityManager: Spawned entity "${entity.name}" (${entity.id})`);
        return entity;
    }

    /**
     * Spawn entity without model (for custom mesh entities)
     */
    public async spawnEmpty(options: EntityConfig): Promise<Entity> {
        const entity = new Entity(this._scene, options);
        this._registerEntity(entity);
        await entity.initialize();
        this._activeEntities.add(entity.id);
        return entity;
    }

    /**
     * Register an existing entity
     */
    private _registerEntity(entity: Entity): void {
        this._entities.set(entity.id, entity);

        // Index by tags
        entity.tags.forEach(tag => {
            if (!this._entitiesByTag.has(tag)) {
                this._entitiesByTag.set(tag, new Set());
            }
            this._entitiesByTag.get(tag)!.add(entity.id);
        });
    }

    /**
     * Get entity by ID
     */
    public getEntity(id: string): Entity | null {
        return this._entities.get(id) || null;
    }

    /**
     * Get all entities with a specific tag
     */
    public getEntitiesByTag(tag: string): Entity[] {
        const ids = this._entitiesByTag.get(tag);
        if (!ids) return [];
        
        return Array.from(ids)
            .map(id => this._entities.get(id))
            .filter((e): e is Entity => e !== undefined);
    }

    /**
     * Get all entities within radius of a point
     */
    public getEntitiesInRadius(center: Vector3, radius: number): Entity[] {
        const radiusSq = radius * radius;
        const results: Entity[] = [];

        this._entities.forEach(entity => {
            if (entity.state !== EntityState.ACTIVE) return;
            
            const distSq = Vector3.DistanceSquared(center, entity.position);
            if (distSq <= radiusSq) {
                results.push(entity);
            }
        });

        return results;
    }

    /**
     * Get nearest entity to a point
     */
    public getNearestEntity(
        point: Vector3, 
        maxDistance?: number,
        filter?: (entity: Entity) => boolean
    ): Entity | null {
        let nearest: Entity | null = null;
        let nearestDistSq = maxDistance ? maxDistance * maxDistance : Infinity;

        this._entities.forEach(entity => {
            if (entity.state !== EntityState.ACTIVE) return;
            if (filter && !filter(entity)) return;

            const distSq = Vector3.DistanceSquared(point, entity.position);
            if (distSq < nearestDistSq) {
                nearestDistSq = distSq;
                nearest = entity;
            }
        });

        return nearest;
    }

    /**
     * Get all active entities
     */
    public getActiveEntities(): Entity[] {
        return Array.from(this._activeEntities)
            .map(id => this._entities.get(id))
            .filter((e): e is Entity => e !== undefined);
    }

    /**
     * Update all active entities
     */
    public update(deltaTime: number): void {
        this._activeEntities.forEach(id => {
            const entity = this._entities.get(id);
            if (entity && entity.state === EntityState.ACTIVE) {
                entity.update(deltaTime);
            }
        });
    }

    /**
     * Destroy an entity
     */
    public destroy(entity: Entity | string): void {
        const id = typeof entity === "string" ? entity : entity.id;
        const target = this._entities.get(id);
        
        if (!target) return;

        // Remove from tag indices
        target.tags.forEach(tag => {
            this._entitiesByTag.get(tag)?.delete(id);
        });

        // Remove from tracking
        this._entities.delete(id);
        this._activeEntities.delete(id);

        // Dispose
        target.dispose();

        console.log(`EntityManager: Destroyed entity "${target.name}" (${id})`);
    }

    /**
     * Destroy all entities with a specific tag
     */
    public destroyByTag(tag: string): void {
        const entities = this.getEntitiesByTag(tag);
        entities.forEach(entity => this.destroy(entity));
    }

    /**
     * Destroy all entities
     */
    public destroyAll(): void {
        this._entities.forEach(entity => entity.dispose());
        this._entities.clear();
        this._entitiesByTag.clear();
        this._activeEntities.clear();
    }

    /**
     * Pause an entity
     */
    public pause(entity: Entity | string): void {
        const target = typeof entity === "string" 
            ? this._entities.get(entity) 
            : entity;
        
        if (target) {
            target.state = EntityState.PAUSED;
        }
    }

    /**
     * Resume a paused entity
     */
    public resume(entity: Entity | string): void {
        const target = typeof entity === "string" 
            ? this._entities.get(entity) 
            : entity;
        
        if (target && target.state === EntityState.PAUSED) {
            target.state = EntityState.ACTIVE;
        }
    }

    /**
     * Dispose entity manager
     */
    public dispose(): void {
        this.destroyAll();
    }
}
